import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControl, IconButton, InputLabel, MenuItem, Select, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, Tab, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography, alpha,
} from '@mui/material'
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined'
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined'
import StackedLineChartOutlinedIcon from '@mui/icons-material/StackedLineChartOutlined'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined'
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined'
import { LineChart } from '@mui/x-charts/LineChart'
import { BarChart } from '@mui/x-charts/BarChart'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { AssetSoftPoint, AssetSoftPointReading } from '@cmms/types'

import { api } from '../../../lib/api'
import { useOrgSettings } from '../../../hooks/useOrgSettings'
import { usePermissions } from '../../../hooks/usePermissions'
import { toast } from '../../../store/snackbarStore'
import { useConfirm } from '../../../components/ConfirmDialog'

const PALETTE = ['#6366F1', '#10B981', '#F59E0B', '#EF4444', '#0EA5E9', '#EC4899', '#8B5CF6', '#14B8A6']

type ChartType = 'line' | 'bar' | 'area'
type ViewMode = 'chart' | 'table'
type RangeKey = '24h' | '7d' | '30d' | '90d' | 'all'

const RANGE_MS: Partial<Record<RangeKey, number>> = {
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
  '90d': 90 * 24 * 60 * 60 * 1000,
}

const chartTooltipSx = {
  '& .MuiChartsTooltip-table': { fontSize: 12 },
}

interface PointSeries {
  pointId: string
  point: AssetSoftPoint
  // Ascending (recorded_at, value) pairs — already delta-transformed if applicable.
  // `id` is the underlying reading's id, needed to target an edit — null
  // in delta mode, where each point is a *difference* between two
  // readings rather than one reading itself, so there's nothing single
  // to edit (editing is only offered on the raw values view).
  values: { t: string; v: number; id: string | null }[]
}

/**
 * Charts + table for asset soft-point trend history. One request per
 * selected point (readings endpoint is per-point) merged onto a shared,
 * sorted set of timestamps so multiple series can share one x-axis —
 * points on the same telemetry checklist are usually recorded at the
 * exact same instant (one `submittedAt` per submission), but nothing
 * here assumes that: a series simply has a gap (`null`) at any
 * timestamp it has no reading for.
 */
export function AssetTrendsPanel({ assetId }: { assetId: string }) {
  const { formatDate } = useOrgSettings()
  const { can } = usePermissions()
  const canEditTelemetry = can('assets:edit_telemetry')
  const queryClient = useQueryClient()

  const pointsQ = useQuery({
    queryKey: ['asset-soft-points', assetId],
    queryFn: () => api.assets.listSoftPoints(assetId),
  })
  const points = useMemo(() => Array.isArray(pointsQ.data) ? pointsQ.data : [], [pointsQ.data])

  const [selected, setSelected] = useState<string[]>([])
  const didInit = useRef(false)
  useEffect(() => {
    if (!didInit.current && points.length > 0) {
      setSelected(points.map((p) => p.id))
      didInit.current = true
    }
  }, [points])

  const [chartType, setChartType] = useState<ChartType>('line')
  const [viewMode, setViewMode] = useState<ViewMode>('chart')
  const [range, setRange] = useState<RangeKey>('30d')
  const [deltaMode, setDeltaMode] = useState(false)

  // Memoized on `range` alone — computing this inline from Date.now() on
  // every render produced a new millisecond-precision value each time,
  // which changed the query key below on every render and caused an
  // infinite refetch loop (each fetch triggers a re-render, which
  // recomputes `since`, which is a "new" query, forever).
  const since = useMemo(
    () => range === 'all' ? undefined : new Date(Date.now() - (RANGE_MS[range] ?? 0)).toISOString(),
    [range],
  )

  const readingsQueries = useQueries({
    queries: selected.map((pointId) => ({
      queryKey: ['asset-soft-point-readings', pointId, since ?? 'all'],
      queryFn: () => api.assets.listSoftPointReadings(assetId, pointId, { since, limit: 1000 }),
    })),
  })
  const isLoadingReadings = readingsQueries.some((q) => q.isLoading)

  function togglePoint(id: string) {
    setSelected((prev) => prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id])
  }

  const series: PointSeries[] = useMemo(() => {
    return selected.map((pointId, i) => {
      const point = points.find((p) => p.id === pointId)!
      const raw = readingsQueries[i]?.data
      const rows: AssetSoftPointReading[] = Array.isArray(raw) ? [...raw].reverse() : [] // API returns DESC → ascending for charting
      const values = deltaMode
        ? rows.slice(1).map((r, idx) => ({ t: r.recorded_at, v: Number(r.value) - Number(rows[idx]!.value), id: null }))
        : rows.map((r) => ({ t: r.recorded_at, v: Number(r.value), id: r.id }))
      return { pointId, point, values }
    }).filter((s) => s.point)
    // readingsQueries is a fresh array every render (useQueries); its
    // .data contents are what actually matter here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, points, deltaMode, ...readingsQueries.map((q) => q.data)])

  const xTimestamps = useMemo(() => {
    const set = new Set<string>()
    series.forEach((s) => s.values.forEach((v) => set.add(v.t)))
    return Array.from(set).sort()
  }, [series])

  const xLabels = useMemo(
    () => xTimestamps.map((t) => formatDate(t, range === '24h' || range === '7d')),
    [xTimestamps, formatDate, range],
  )

  const chartSeries = useMemo(() => series.map((s, idx) => {
    const valueMap = new Map(s.values.map((v) => [v.t, v.v]))
    const idMap = new Map(s.values.map((v) => [v.t, v.id]))
    return {
      pointId: s.pointId,
      label: s.point.name,
      unit: s.point.unit,
      color: PALETTE[idx % PALETTE.length],
      data: xTimestamps.map((t) => valueMap.has(t) ? valueMap.get(t)! : null),
      readingIds: xTimestamps.map((t) => idMap.get(t) ?? null),
    }
  }), [series, xTimestamps])

  const hasData = xTimestamps.length > 0

  // A point can have readings that just don't fall in the currently
  // selected range (e.g. a device posted with an old/test timestamp) —
  // `last_recorded_at` is set the moment ANY reading lands, regardless
  // of range, so its presence here means "there's data, just not here"
  // rather than "nothing has ever been recorded".
  const hasReadingsOutsideRange = range !== 'all' && selected.some(
    (id) => points.find((p) => p.id === id)?.last_recorded_at,
  )

  // Corrects a mis-entered reading value — table view only (editing a
  // point on a shared chart has no obvious target cell). Requires
  // `assets:edit_telemetry`, a separate permission from the one that
  // covers editing a point's own name/unit, since silently rewriting
  // recorded history is more sensitive than renaming a channel.
  const [editTarget, setEditTarget] = useState<{
    pointId: string; pointName: string; unit: string | null
    readingId: string; timestamp: string; value: number
  } | null>(null)
  const [editValue, setEditValue] = useState('')

  const editM = useMutation({
    mutationFn: () => api.assets.updateSoftPointReading(assetId, editTarget!.pointId, editTarget!.readingId, {
      value: Number(editValue),
    }),
    onSuccess: () => {
      toast.success('Reading updated')
      queryClient.invalidateQueries({ queryKey: ['asset-soft-point-readings', editTarget!.pointId] })
      queryClient.invalidateQueries({ queryKey: ['asset-soft-points', assetId] })
      setEditTarget(null)
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to update reading'),
  })

  function openEdit(pointId: string, pointName: string, unit: string | null, readingId: string, timestamp: string, value: number) {
    setEditTarget({ pointId, pointName, unit, readingId, timestamp, value })
    setEditValue(String(value))
  }

  const confirmDlg = useConfirm()

  function invalidateReadings(pointId: string) {
    queryClient.invalidateQueries({ queryKey: ['asset-soft-point-readings', pointId] })
    queryClient.invalidateQueries({ queryKey: ['asset-soft-points', assetId] })
  }

  const deleteReadingM = useMutation({
    mutationFn: ({ pointId, readingId }: { pointId: string; readingId: string }) =>
      api.assets.deleteSoftPointReading(assetId, pointId, readingId),
    onSuccess: (_data, { pointId }) => { toast.success('Reading deleted'); invalidateReadings(pointId) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to delete reading'),
  })

  async function handleDeleteReading(pointId: string, readingId: string) {
    const ok = await confirmDlg({
      title: 'Delete this reading?',
      message: 'This removes it from the trend history permanently.',
      confirmLabel: 'Delete',
      variant: 'danger',
    })
    if (ok) deleteReadingM.mutate({ pointId, readingId })
  }

  const [addOpen, setAddOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [clearOpen, setClearOpen] = useState(false)

  return (
    <Box sx={{ px: 3.5, py: 3 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <Box sx={{
          width: 38, height: 38, borderRadius: 2,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: alpha('#7C3AED', 0.12), color: '#7C3AED',
        }}>
          <TimelineOutlinedIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em' }}>
            Soft point trends
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            Values recorded via checklist readings over time
          </Typography>
        </Box>
        {canEditTelemetry && points.length > 0 && (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Add a reading">
              <IconButton size="small" onClick={() => setAddOpen(true)}>
                <AddCircleOutlineIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Import readings from CSV">
              <IconButton size="small" onClick={() => setImportOpen(true)}>
                <UploadFileOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Clear readings">
              <IconButton size="small" onClick={() => setClearOpen(true)}>
                <DeleteSweepOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Stack>

      {points.length === 0 ? (
        <Box sx={{ py: 8, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
          <TimelineOutlinedIcon sx={{ fontSize: 28, color: 'text.disabled', mb: 1 }} />
          <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>No soft points on this asset yet</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
            Add some on the Telemetry tab, then generate a telemetry checklist for them.
          </Typography>
        </Box>
      ) : (
        <>
          {/* Point multi-select */}
          <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 2 }}>
            {points.map((p, idx) => {
              const active = selected.includes(p.id)
              const colorIdx = selected.indexOf(p.id) >= 0 ? selected.indexOf(p.id) % PALETTE.length : idx % PALETTE.length
              const color = PALETTE[colorIdx] ?? '#64748B'
              return (
                <Chip
                  key={p.id}
                  label={p.name}
                  size="small"
                  onClick={() => togglePoint(p.id)}
                  sx={{
                    height: 26, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    bgcolor: active ? alpha(color, 0.15) : 'action.hover',
                    color: active ? color : 'text.secondary',
                    border: '1px solid', borderColor: active ? alpha(color, 0.4) : 'divider',
                    '&:hover': { borderColor: color },
                  }}
                />
              )
            })}
          </Stack>

          {/* Controls */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
            <Tabs value={range} onChange={(_, v) => setRange(v)} sx={{ minHeight: 28, '& .MuiTab-root': { minHeight: 28, px: 1.25, fontSize: 12 } }}>
              <Tab value="24h" label="24h" />
              <Tab value="7d" label="7d" />
              <Tab value="30d" label="30d" />
              <Tab value="90d" label="90d" />
              <Tab value="all" label="All" />
            </Tabs>

            <Stack direction="row" alignItems="center" spacing={1}>
              <Tooltip title="Show the difference between consecutive readings instead of the raw value — useful for cumulative meters like kWh." arrow>
                <ToggleButton
                  value="delta" selected={deltaMode} onChange={() => setDeltaMode((v) => !v)}
                  size="small" sx={{ height: 30, px: 1.25, fontSize: 11.5, fontWeight: 600, textTransform: 'none', gap: 0.5 }}
                >
                  <CompareArrowsOutlinedIcon sx={{ fontSize: 15 }} /> Consumption (Δ)
                </ToggleButton>
              </Tooltip>

              {viewMode === 'chart' && (
                <ToggleButtonGroup size="small" value={chartType} exclusive onChange={(_, v) => v && setChartType(v)}>
                  <ToggleButton value="line" sx={{ height: 30, px: 1 }}><Tooltip title="Line"><ShowChartOutlinedIcon sx={{ fontSize: 16 }} /></Tooltip></ToggleButton>
                  <ToggleButton value="area" sx={{ height: 30, px: 1 }}><Tooltip title="Area"><StackedLineChartOutlinedIcon sx={{ fontSize: 16 }} /></Tooltip></ToggleButton>
                  <ToggleButton value="bar" sx={{ height: 30, px: 1 }}><Tooltip title="Bar"><BarChartOutlinedIcon sx={{ fontSize: 16 }} /></Tooltip></ToggleButton>
                </ToggleButtonGroup>
              )}

              <ToggleButtonGroup size="small" value={viewMode} exclusive onChange={(_, v) => v && setViewMode(v)}>
                <ToggleButton value="chart" sx={{ height: 30, px: 1 }}><Tooltip title="Chart"><ShowChartOutlinedIcon sx={{ fontSize: 16 }} /></Tooltip></ToggleButton>
                <ToggleButton value="table" sx={{ height: 30, px: 1 }}><Tooltip title="Table"><TableChartOutlinedIcon sx={{ fontSize: 16 }} /></Tooltip></ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Stack>

          {selected.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Select at least one point above</Typography>
            </Box>
          ) : isLoadingReadings ? (
            <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress size={24} /></Box>
          ) : !hasData ? (
            <Box sx={{ py: 8, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>No readings in this range</Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
                {hasReadingsOutsideRange
                  ? "This point has recorded readings — they're just outside the selected time range."
                  : 'Try a wider range, or check that the linked checklist has been submitted.'}
              </Typography>
              {hasReadingsOutsideRange && (
                <Button size="small" sx={{ mt: 1.5 }} onClick={() => setRange('all')}>
                  Show all history
                </Button>
              )}
            </Box>
          ) : viewMode === 'table' ? (
            <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: 11.5, fontWeight: 700 }}>Time</TableCell>
                    {chartSeries.map((s) => (
                      <TableCell key={s.pointId} align="right" sx={{ fontSize: 11.5, fontWeight: 700, color: s.color }}>
                        {s.label}{s.unit ? ` (${s.unit})` : ''}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {xTimestamps.slice().reverse().map((t, revIdx) => {
                    const i = xTimestamps.length - 1 - revIdx
                    return (
                      <TableRow key={t} hover>
                        <TableCell sx={{ fontSize: 12 }}>{formatDate(t, true)}</TableCell>
                        {chartSeries.map((s) => {
                          const value = s.data[i]
                          const readingId = s.readingIds[i]
                          const editable = canEditTelemetry && value != null && readingId != null
                          return (
                            <TableCell key={s.pointId} align="right" sx={{ fontSize: 12 }}>
                              {value == null ? '—' : (
                                editable ? (
                                  <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.25}
                                    className="reading-cell" sx={{ '&:hover .reading-edit-btn': { opacity: 1 } }}>
                                    <span>{value.toLocaleString(undefined, { maximumFractionDigits: 3 })}</span>
                                    <IconButton
                                      className="reading-edit-btn"
                                      size="small"
                                      sx={{ p: 0.25, opacity: 0, transition: 'opacity 120ms ease' }}
                                      onClick={() => openEdit(s.pointId, s.label, s.unit, readingId, t, value)}
                                    >
                                      <EditOutlinedIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                    <IconButton
                                      className="reading-edit-btn"
                                      size="small"
                                      sx={{ p: 0.25, opacity: 0, transition: 'opacity 120ms ease' }}
                                      onClick={() => handleDeleteReading(s.pointId, readingId)}
                                    >
                                      <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Stack>
                                ) : value.toLocaleString(undefined, { maximumFractionDigits: 3 })
                              )}
                            </TableCell>
                          )
                        })}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ height: 340 }}>
              {chartType === 'bar' ? (
                <BarChart
                  series={chartSeries.map((s) => ({ id: s.pointId, data: s.data, label: s.label, color: s.color, valueFormatter: (v: number | null) => v == null ? '' : `${v.toLocaleString()}${s.unit ? ` ${s.unit}` : ''}` }))}
                  xAxis={[{ scaleType: 'band', data: xLabels }]}
                  margin={{ top: 10, right: 16, bottom: 24, left: 48 }}
                  hideLegend={chartSeries.length <= 1}
                  sx={{
                    '& .MuiChartsAxis-tickLabel': { fill: (t) => t.palette.text.secondary, fontSize: 10.5 },
                    '& .MuiChartsGrid-line': { stroke: (t) => t.palette.divider, strokeDasharray: '2 4' },
                    ...chartTooltipSx,
                  }}
                />
              ) : (
                <LineChart
                  series={chartSeries.map((s) => ({
                    id: s.pointId, data: s.data, label: s.label, color: s.color,
                    area: chartType === 'area', showMark: xLabels.length <= 40, curve: 'monotoneX',
                    connectNulls: false,
                    valueFormatter: (v: number | null) => v == null ? '' : `${v.toLocaleString()}${s.unit ? ` ${s.unit}` : ''}`,
                  }))}
                  xAxis={[{ scaleType: 'point', data: xLabels }]}
                  margin={{ top: 10, right: 16, bottom: 24, left: 48 }}
                  hideLegend={chartSeries.length <= 1}
                  axisHighlight={{ x: 'line' }}
                  sx={{
                    '& .MuiChartsAxis-tickLabel': { fill: (t) => t.palette.text.secondary, fontSize: 10.5 },
                    '& .MuiChartsGrid-line': { stroke: (t) => t.palette.divider, strokeDasharray: '2 4' },
                    '& .MuiChartsAxisHighlight-root': { stroke: (t) => t.palette.text.disabled, strokeDasharray: '2 3' },
                    ...chartTooltipSx,
                  }}
                />
              )}
            </Box>
          )}
        </>
      )}

      <Dialog open={editTarget != null} onClose={() => setEditTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Correct reading</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {editTarget && (
            <>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                {editTarget.pointName} · {formatDate(editTarget.timestamp, true)}
              </Typography>
              <TextField
                label="Value" required autoFocus fullWidth size="small" type="number"
                value={editValue} onChange={(e) => setEditValue(e.target.value)}
                slotProps={{ input: { endAdornment: editTarget.unit || undefined } }}
              />
              <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>
                This only corrects the recorded value — it won't reopen the checklist
                or change who submitted it.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={editValue.trim() === '' || Number.isNaN(Number(editValue)) || editM.isPending}
            onClick={() => editM.mutate()}
          >
            {editM.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <AddReadingDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        assetId={assetId}
        points={points}
        defaultPointId={selected[0] ?? points[0]?.id}
        onAdded={(pointId) => { invalidateReadings(pointId); setAddOpen(false) }}
      />
      <ImportReadingsDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        assetId={assetId}
        points={points}
        defaultPointId={selected[0] ?? points[0]?.id}
        onImported={(pointId) => { invalidateReadings(pointId); setImportOpen(false) }}
      />
      <ClearReadingsDialog
        open={clearOpen}
        onClose={() => setClearOpen(false)}
        assetId={assetId}
        points={points}
        defaultPointId={selected[0] ?? points[0]?.id}
        onCleared={(pointId) => { invalidateReadings(pointId); setClearOpen(false) }}
      />
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// Add / import / clear dialogs — each is scoped to a single soft point
// (matching the point-scoped backend endpoints), with a Point select at
// the top since the panel above can have several points graphed at once.
// ═══════════════════════════════════════════════════════════════════════════

function PointSelect({ points, value, onChange }: {
  points: AssetSoftPoint[]; value: string; onChange: (id: string) => void
}) {
  return (
    <FormControl size="small" fullWidth>
      <InputLabel id="trend-point-select-label">Point</InputLabel>
      <Select
        labelId="trend-point-select-label"
        label="Point"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        renderValue={(id) => {
          const p = points.find((pt) => pt.id === id)
          if (!p) return ''
          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              <span>{p.name}</span>
              {p.unit && (
                <Typography component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>{p.unit}</Typography>
              )}
            </Stack>
          )
        }}
      >
        {points.map((p) => (
          <MenuItem key={p.id} value={p.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <span>{p.name}</span>
            {p.unit && <Typography component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>{p.unit}</Typography>}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

function AddReadingDialog({ open, onClose, assetId, points, defaultPointId, onAdded }: {
  open: boolean
  onClose: () => void
  assetId: string
  points: AssetSoftPoint[]
  defaultPointId?: string
  onAdded: (pointId: string) => void
}) {
  const [pointId, setPointId] = useState(defaultPointId ?? '')
  const [value, setValue] = useState('')
  const [when, setWhen] = useState('')

  useEffect(() => {
    if (open) {
      setPointId(defaultPointId ?? points[0]?.id ?? '')
      setValue('')
      setWhen('')
    }
  }, [open, defaultPointId, points])

  const addM = useMutation({
    mutationFn: () => api.assets.createSoftPointReading(assetId, pointId, {
      value: Number(value),
      recorded_at: when ? new Date(when).toISOString() : undefined,
    }),
    onSuccess: () => { toast.success('Reading added'); onAdded(pointId) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to add reading'),
  })

  const point = points.find((p) => p.id === pointId)
  const canSubmit = pointId && value.trim() !== '' && !Number.isNaN(Number(value)) && !addM.isPending

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Add a reading</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <PointSelect points={points} value={pointId} onChange={setPointId} />
        <TextField
          label="Value" required autoFocus fullWidth size="small" type="number"
          value={value} onChange={(e) => setValue(e.target.value)}
          slotProps={{ input: { endAdornment: point?.unit || undefined } }}
        />
        <TextField
          label="Time" fullWidth size="small" type="datetime-local"
          value={when} onChange={(e) => setWhen(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          helperText="Leave blank to use the current time"
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit} onClick={() => addM.mutate()}>
          {addM.isPending ? 'Adding…' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

interface ImportRow { _row: number; value: number | null; recorded_at: string | null; _error?: string }

function ImportReadingsDialog({ open, onClose, assetId, points, defaultPointId, onImported }: {
  open: boolean
  onClose: () => void
  assetId: string
  points: AssetSoftPoint[]
  defaultPointId?: string
  onImported: (pointId: string) => void
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [pointId, setPointId] = useState(defaultPointId ?? '')
  const [file, setFile] = useState<File | null>(null)
  const [parseError, setParseError] = useState<string | null>(null)
  const [rows, setRows] = useState<ImportRow[]>([])
  const [serverErrors, setServerErrors] = useState<Record<number, string>>({})

  useEffect(() => {
    if (open) {
      setPointId(defaultPointId ?? points[0]?.id ?? '')
      setFile(null); setParseError(null); setRows([]); setServerErrors({})
    }
  }, [open, defaultPointId, points])

  const importM = useMutation({
    mutationFn: (payload: { value: number; recorded_at: string }[]) =>
      api.assets.importSoftPointReadings(assetId, pointId, payload),
    onSuccess: (r) => {
      toast.success(`Imported ${r.imported} reading${r.imported === 1 ? '' : 's'}`)
      onImported(pointId)
    },
    onError: (err) => {
      if (err instanceof ApiError && err.rowErrors?.length) {
        const map: Record<number, string> = {}
        for (const e of err.rowErrors) map[e.row] = e.message
        setServerErrors(map)
        toast.error(`${err.rowErrors.length} row${err.rowErrors.length === 1 ? '' : 's'} rejected — see below`)
        return
      }
      toast.error(err instanceof ApiError ? err.message : 'Import failed')
    },
  })

  async function handleFilePicked(f: File | undefined | null) {
    if (!f) return
    setFile(f); setServerErrors({}); setRows([]); setParseError(null)
    const text = await f.text()
    const parsed = parseReadingsCsv(text)
    if (parsed.headerError) { setParseError(parsed.headerError); return }
    setRows(parsed.rows)
  }

  const validRows = useMemo(() => rows.filter((r) => !r._error && !serverErrors[r._row]), [rows, serverErrors])
  const invalidRows = useMemo(() => rows.filter((r) => r._error || serverErrors[r._row]), [rows, serverErrors])
  const canImport = pointId && validRows.length > 0 && invalidRows.length === 0 && !importM.isPending

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { minHeight: '55vh' } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Import readings from CSV</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <PointSelect points={points} value={pointId} onChange={setPointId} />

        <Alert severity="info" sx={{ borderRadius: 2, '& .MuiAlert-message': { width: '100%' } }}>
          <Typography sx={{ fontSize: 12.5 }} component="div">
            Two columns: <code>timestamp</code> (any parseable date/time) and <code>value</code> (a number).
            First row must be the header.
          </Typography>
        </Alert>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="stretch">
          <Button
            variant="outlined" startIcon={<DownloadOutlinedIcon />}
            onClick={downloadReadingsCsvTemplate}
            sx={{ borderRadius: 2, fontWeight: 600, whiteSpace: 'nowrap' }}
          >
            Download template
          </Button>
          <Button
            variant="contained" startIcon={<UploadFileOutlinedIcon />}
            onClick={() => fileInputRef.current?.click()}
            sx={{ borderRadius: 2, fontWeight: 600, whiteSpace: 'nowrap' }}
          >
            {file ? 'Choose another file' : 'Upload CSV'}
          </Button>
          <input ref={fileInputRef} type="file" accept=".csv,text/csv" hidden
            onChange={(e) => handleFilePicked(e.target.files?.[0])} />
          {file && (
            <Chip
              label={file.name} onDelete={() => { setFile(null); setRows([]) }}
              sx={{ alignSelf: 'center', fontWeight: 600, maxWidth: { xs: '100%', sm: 220 } }}
            />
          )}
        </Stack>

        {parseError && <Alert severity="error" sx={{ borderRadius: 2 }}>{parseError}</Alert>}

        {rows.length > 0 && (
          <>
            <Divider />
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography sx={{ fontWeight: 700, fontSize: 13 }}>Preview</Typography>
              <Chip size="small" color="success" label={`${validRows.length} valid`} sx={{ fontWeight: 600 }} />
              {invalidRows.length > 0 && (
                <Chip size="small" color="error" label={`${invalidRows.length} invalid`} sx={{ fontWeight: 600 }} />
              )}
            </Stack>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, maxHeight: 260, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: 11.5, fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ fontSize: 11.5, fontWeight: 700 }}>Timestamp</TableCell>
                    <TableCell align="right" sx={{ fontSize: 11.5, fontWeight: 700 }}>Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => {
                    const err = r._error || serverErrors[r._row]
                    return (
                      <TableRow key={r._row} sx={{ bgcolor: err ? '#FEF2F2' : 'transparent' }}>
                        <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{r._row}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          {r.recorded_at ?? '—'}
                          {err && <Typography sx={{ fontSize: 11, color: '#DC2626' }}>{err}</Typography>}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>{r.value ?? '—'}</TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canImport}
          onClick={() => importM.mutate(validRows.map((r) => ({ value: r.value!, recorded_at: r.recorded_at! })))}
        >
          {importM.isPending ? 'Importing…' : `Import ${validRows.length} reading${validRows.length === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// Sample timestamps are relative to "now" so the template always
// imports cleanly regardless of when it's downloaded (an absolute
// hardcoded date would eventually look like stale test data, exactly
// the confusion this whole feature exists to fix — see the "recorded
// long back" trend-timestamp bug this session).
function downloadReadingsCsvTemplate() {
  const now = Date.now()
  const iso = (hoursAgo: number) => new Date(now - hoursAgo * 3_600_000).toISOString()
  const header = 'timestamp,value'
  const sample = [
    `${iso(72)},41.2`,
    `${iso(48)},43.8`,
    `${iso(24)},40.5`,
    `${iso(1)},44.1`,
  ].join('\n')
  const blob = new Blob([`${header}\n${sample}\n`], { type: 'text/csv;charset=utf-8' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href     = url
  a.download = 'readings-import-template.csv'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function parseReadingsCsv(text: string): { rows: ImportRow[]; headerError: string | null } {
  const clean = text.replace(/^﻿/, '').replace(/\r\n?/g, '\n').trim()
  if (clean.length === 0) return { rows: [], headerError: 'The file is empty.' }
  const lines = clean.split('\n').filter((l) => l.trim().length > 0)
  if (lines.length < 2) return { rows: [], headerError: 'Need at least a header row + one data row.' }

  const header = lines[0]!.split(',').map((h) => h.trim().toLowerCase())
  const tsIdx = header.indexOf('timestamp')
  const valIdx = header.indexOf('value')
  if (tsIdx < 0 || valIdx < 0) {
    return { rows: [], headerError: 'Header row must include "timestamp" and "value" columns.' }
  }

  const rows: ImportRow[] = []
  for (let i = 1; i < lines.length; i++) {
    const cells = lines[i]!.split(',')
    const tsRaw = cells[tsIdx]?.trim() ?? ''
    const valRaw = cells[valIdx]?.trim() ?? ''
    const row: ImportRow = { _row: i + 1, value: null, recorded_at: null }

    const num = Number(valRaw)
    if (valRaw === '' || !Number.isFinite(num)) {
      row._error = 'value must be a number'
    } else {
      row.value = num
    }

    const date = new Date(tsRaw)
    if (!tsRaw || Number.isNaN(date.getTime())) {
      row._error = row._error ?? 'timestamp must be a valid date/time'
    } else {
      row.recorded_at = date.toISOString()
    }
    rows.push(row)
  }
  return { rows, headerError: null }
}

function ClearReadingsDialog({ open, onClose, assetId, points, defaultPointId, onCleared }: {
  open: boolean
  onClose: () => void
  assetId: string
  points: AssetSoftPoint[]
  defaultPointId?: string
  onCleared: (pointId: string) => void
}) {
  const confirmDlg = useConfirm()
  const [pointId, setPointId] = useState(defaultPointId ?? '')
  const [scope, setScope] = useState<'all' | 'range'>('all')
  const [since, setSince] = useState('')
  const [until, setUntil] = useState('')

  useEffect(() => {
    if (open) {
      setPointId(defaultPointId ?? points[0]?.id ?? '')
      setScope('all'); setSince(''); setUntil('')
    }
  }, [open, defaultPointId, points])

  const clearM = useMutation({
    mutationFn: () => api.assets.clearSoftPointReadings(assetId, pointId, {
      since: scope === 'range' && since ? new Date(since).toISOString() : undefined,
      until: scope === 'range' && until ? new Date(until).toISOString() : undefined,
    }),
    onSuccess: (r) => {
      toast.success(`Deleted ${r.deleted} reading${r.deleted === 1 ? '' : 's'}`)
      onCleared(pointId)
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to clear readings'),
  })

  const point = points.find((p) => p.id === pointId)
  const canSubmit = pointId && (scope === 'all' || since || until) && !clearM.isPending

  async function handleClear() {
    const ok = await confirmDlg({
      title: 'Clear this trend data?',
      message: scope === 'all'
        ? `This permanently deletes every recorded reading for "${point?.name}". This cannot be undone.`
        : `This permanently deletes readings for "${point?.name}" in the selected range. This cannot be undone.`,
      confirmLabel: 'Delete',
      variant: 'danger',
    })
    if (ok) clearM.mutate()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Clear readings</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <PointSelect points={points} value={pointId} onChange={setPointId} />
        <Box>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>
            Scope
          </Typography>
          <ToggleButtonGroup size="small" value={scope} exclusive onChange={(_, v) => v && setScope(v)} fullWidth>
            <ToggleButton value="all" sx={{ fontSize: 12, textTransform: 'none' }}>All time</ToggleButton>
            <ToggleButton value="range" sx={{ fontSize: 12, textTransform: 'none' }}>Date range</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        {scope === 'range' && (
          <Stack direction="row" spacing={1.5}>
            <TextField
              label="From" fullWidth size="small" type="datetime-local"
              value={since} onChange={(e) => setSince(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="To" fullWidth size="small" type="datetime-local"
              value={until} onChange={(e) => setUntil(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Stack>
        )}
        <Alert severity="warning" sx={{ borderRadius: 2, '& .MuiAlert-message': { fontSize: 12.5 } }}>
          This permanently deletes data — it can't be undone.
        </Alert>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="error" disabled={!canSubmit} onClick={handleClear}>
          {clearM.isPending ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
